import { useState, useEffect, useRef } from "react";
import { useTenant } from "@/hooks/useTenant";
import { useAuth } from "@/hooks/useAuth";
import { TenantService } from "@/services/tenantService";
import { FileUploadService } from "@/services/fileUploadService";
import { SupabaseImageService } from "@/services/supabaseImageService";
import type { UpdateTenantSettingsData } from "@/types/tenant";
import toast from "react-hot-toast";
import { TbUpload, TbTrash } from "react-icons/tb";

export const TenantSettings: React.FC = () => {
  const { tenantId, tenantSettings, refreshTenant } = useTenant();
  const { hasPlatformRole } = useAuth();
  const canEditDomain =
    hasPlatformRole("super_admin") || hasPlatformRole("platform_admin");
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState<
    "branding" | "owner" | "domain" | "features"
  >("branding");

  const [formData, setFormData] = useState<UpdateTenantSettingsData>({
    logo_url: "",
    favicon_url: "",
    site_title: "Taskr",
    primary_color: "#374795",
    accent_color: "#2563eb",
    owner_name: "",
    owner_email: "",
    support_email: "",
    owner_phone: "",
    owner_address: "",
    custom_domain: "",
    subdomain: "",
    redirect_url: "",
    website_url: "",
  });

  // Load existing settings into form
  useEffect(() => {
    if (tenantSettings) {
      setFormData({
        logo_url: tenantSettings.logo_url || "",
        favicon_url: tenantSettings.favicon_url || "",
        site_title: tenantSettings.site_title || "Taskr",
        primary_color: tenantSettings.primary_color || "#374795",
        accent_color: tenantSettings.accent_color || "#2563eb",
        owner_name: tenantSettings.owner_name || "",
        owner_email: tenantSettings.owner_email || "",
        support_email: tenantSettings.support_email || "",
        owner_phone: tenantSettings.owner_phone || "",
        owner_address: tenantSettings.owner_address || "",
        custom_domain: tenantSettings.custom_domain || "",
        subdomain: tenantSettings.subdomain || "",
        redirect_url: tenantSettings.redirect_url || "",
        website_url: tenantSettings.website_url || "",
      });
    }
  }, [tenantSettings]);

  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [faviconUploading, setFaviconUploading] = useState(false);
  const [faviconPreviewUrl, setFaviconPreviewUrl] = useState<string | null>(
    null
  );
  const faviconInputRef = useRef<HTMLInputElement>(null);

  // Helper to resolve a storage path or full URL to a signed URL
  const resolveStorageUrl = (
    raw: string,
    setter: (url: string | null) => void
  ) => {
    if (!raw) {
      setter(null);
      return;
    }
    if (raw.startsWith("blob:")) {
      setter(raw);
      return;
    }
    const path = raw.includes("/storage/v1/object/public/task-files/")
      ? raw.split("/storage/v1/object/public/task-files/")[1]
      : raw;
    SupabaseImageService.getSignedUrl(path).then((url) => setter(url));
  };

  // Resolve stored logo path to a signed URL for display
  useEffect(() => {
    resolveStorageUrl(formData.logo_url || "", setLogoPreviewUrl);
  }, [formData.logo_url]);

  // Resolve stored favicon path to a signed URL for display
  useEffect(() => {
    resolveStorageUrl(formData.favicon_url || "", setFaviconPreviewUrl);
  }, [formData.favicon_url]);

  const handleChange = (
    field: keyof UpdateTenantSettingsData,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (
    file: File,
    folder: string,
    field: "logo_url" | "favicon_url",
    setPreview: (url: string | null) => void,
    setUploading: (v: boolean) => void
  ) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed");
      return;
    }
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const result = await FileUploadService.uploadFile(
        file,
        `tenants/${tenantId}/${folder}`
      );
      setFormData((prev) => ({ ...prev, [field]: result.path }));
      toast.success(
        `${field === "logo_url" ? "Logo" : "Favicon"} uploaded`
      );
    } catch (error) {
      setPreview(null);
      toast.error(
        error instanceof Error ? error.message : "Failed to upload"
      );
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (
    field: "logo_url" | "favicon_url",
    setPreview: (url: string | null) => void,
    inputRef: React.RefObject<HTMLInputElement | null>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: "" }));
    setPreview(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleSave = async () => {
    if (!tenantId) {
      toast.error("No tenant context available");
      return;
    }

    setSaving(true);
    try {
      // Normalize URLs: strip full public URL prefix to storage path
      const dataToSave = { ...formData };
      const stripPrefix = (url: string | null | undefined) =>
        url?.includes("/storage/v1/object/public/task-files/")
          ? url.split("/storage/v1/object/public/task-files/")[1]
          : url;
      dataToSave.logo_url = stripPrefix(dataToSave.logo_url) || "";
      dataToSave.favicon_url = stripPrefix(dataToSave.favicon_url) || "";
      await TenantService.updateSettings(tenantId, dataToSave);
      await refreshTenant();
      toast.success("Tenant settings saved successfully");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to save settings"
      );
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    { key: "branding" as const, label: "Branding" },
    { key: "owner" as const, label: "Owner Details" },
    { key: "domain" as const, label: "Domain & URLs" },
  ];

  if (!tenantId) {
    return (
      <div className="p-4 text-center text-gray-500">
        <p>No tenant context available.</p>
        <p className="text-sm mt-1">
          Tenant settings are available when accessing the app via a tenant
          subdomain.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-h-[calc(100vh-320px)] overflow-y-auto">
      {/* Section tabs */}
      <div className="flex gap-2 mb-4">
        {sections.map((section) => (
          <button
            key={section.key}
            onClick={() => setActiveSection(section.key)}
            className={`cursor-pointer px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              activeSection === section.key
                ? "bg-[#374795] text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {section.label}
          </button>
        ))}
      </div>

      {/* Branding Section */}
      {activeSection === "branding" && (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Site Title
            </label>
            <input
              type="text"
              value={formData.site_title || ""}
              onChange={(e) => handleChange("site_title", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent"
              placeholder="Your organization name"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Logo
            </label>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file)
                  handleImageUpload(
                    file,
                    "logos",
                    "logo_url",
                    setLogoPreviewUrl,
                    setLogoUploading
                  );
              }}
            />
            {formData.logo_url ? (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                {logoPreviewUrl ? (
                  <img
                    src={logoPreviewUrl}
                    alt="Logo preview"
                    className="h-10 object-contain"
                  />
                ) : (
                  <div className="h-10 w-20 bg-gray-200 rounded animate-pulse" />
                )}
                <div className="flex gap-2 ml-auto">
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={logoUploading}
                    className="cursor-pointer px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                  >
                    {logoUploading ? "Uploading..." : "Change"}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleRemoveImage(
                        "logo_url",
                        setLogoPreviewUrl,
                        logoInputRef
                      )
                    }
                    className="cursor-pointer p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove logo"
                  >
                    <TbTrash size={16} />
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                disabled={logoUploading}
                className="cursor-pointer w-full flex items-center justify-center gap-2 px-3 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-[#374795] hover:text-[#374795] transition-colors disabled:opacity-50"
              >
                <TbUpload size={18} />
                {logoUploading ? "Uploading..." : "Upload Logo"}
              </button>
            )}
            <p className="mt-1 text-xs text-gray-400">
              JPG, PNG or GIF. Max 10MB.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Favicon
            </label>
            <input
              ref={faviconInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/x-icon,image/vnd.microsoft.icon"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file)
                  handleImageUpload(
                    file,
                    "favicons",
                    "favicon_url",
                    setFaviconPreviewUrl,
                    setFaviconUploading
                  );
              }}
            />
            {formData.favicon_url ? (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                {faviconPreviewUrl ? (
                  <img
                    src={faviconPreviewUrl}
                    alt="Favicon preview"
                    className="h-8 w-8 object-contain"
                  />
                ) : (
                  <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
                )}
                <div className="flex gap-2 ml-auto">
                  <button
                    type="button"
                    onClick={() => faviconInputRef.current?.click()}
                    disabled={faviconUploading}
                    className="cursor-pointer px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                  >
                    {faviconUploading ? "Uploading..." : "Change"}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleRemoveImage(
                        "favicon_url",
                        setFaviconPreviewUrl,
                        faviconInputRef
                      )
                    }
                    className="cursor-pointer p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove favicon"
                  >
                    <TbTrash size={16} />
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => faviconInputRef.current?.click()}
                disabled={faviconUploading}
                className="cursor-pointer w-full flex items-center justify-center gap-2 px-3 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-[#374795] hover:text-[#374795] transition-colors disabled:opacity-50"
              >
                <TbUpload size={18} />
                {faviconUploading ? "Uploading..." : "Upload Favicon"}
              </button>
            )}
            <p className="mt-1 text-xs text-gray-400">
              ICO, PNG, JPG or GIF. Max 10MB.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Primary Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={formData.primary_color || "#374795"}
                  onChange={(e) =>
                    handleChange("primary_color", e.target.value)
                  }
                  className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.primary_color || ""}
                  onChange={(e) =>
                    handleChange("primary_color", e.target.value)
                  }
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent"
                  placeholder="#374795"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Accent Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={formData.accent_color || "#2563eb"}
                  onChange={(e) => handleChange("accent_color", e.target.value)}
                  className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.accent_color || ""}
                  onChange={(e) => handleChange("accent_color", e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent"
                  placeholder="#2563eb"
                />
              </div>
            </div>
          </div>

          {/* Color Preview */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs font-medium text-gray-500 mb-2">Preview</p>
            <div className="flex items-center gap-3">
              <div
                className="w-20 h-8 rounded"
                style={{ backgroundColor: formData.primary_color || "#374795" }}
              />
              <span className="text-xs text-gray-500">Primary</span>
              <div
                className="w-20 h-8 rounded"
                style={{ backgroundColor: formData.accent_color || "#2563eb" }}
              />
              <span className="text-xs text-gray-500">Accent</span>
            </div>
          </div>
        </div>
      )}

      {/* Owner Details Section */}
      {activeSection === "owner" && (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Organization / Owner Name
            </label>
            <input
              type="text"
              value={formData.owner_name || ""}
              onChange={(e) => handleChange("owner_name", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent"
              placeholder="Acme Corporation"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Owner Email
              </label>
              <input
                type="email"
                value={formData.owner_email || ""}
                onChange={(e) => handleChange("owner_email", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent"
                placeholder="admin@acme.com"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Support Email
              </label>
              <input
                type="email"
                value={formData.support_email || ""}
                onChange={(e) => handleChange("support_email", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent"
                placeholder="support@acme.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              value={formData.owner_phone || ""}
              onChange={(e) => handleChange("owner_phone", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent"
              placeholder="+1 (555) 123-4567"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Address
            </label>
            <textarea
              value={formData.owner_address || ""}
              onChange={(e) => handleChange("owner_address", e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent resize-none"
              placeholder="123 Main St, City, State 12345"
            />
          </div>
        </div>
      )}

      {/* Domain & URLs Section */}
      {activeSection === "domain" && (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Subdomain
            </label>
            <div className="flex items-center">
              <input
                type="text"
                value={formData.subdomain || ""}
                onChange={(e) =>
                  handleChange(
                    "subdomain",
                    e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "")
                  )
                }
                disabled={!canEditDomain}
                className={`flex-1 px-3 py-2 border border-gray-300 rounded-l-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent ${!canEditDomain ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}`}
                placeholder="acme"
              />
              <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg text-sm text-gray-500">
                .taskrsol.com
              </span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Custom Domain (optional)
            </label>
            <input
              type="text"
              value={formData.custom_domain || ""}
              onChange={(e) => handleChange("custom_domain", e.target.value)}
              disabled={!canEditDomain}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent ${!canEditDomain ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}`}
              placeholder="tasks.yourdomain.com"
            />
            <p className="mt-1 text-xs text-gray-400">
              {canEditDomain
                ? "Point a CNAME record to your subdomain to use a custom domain."
                : "Only platform administrators can change domain settings."}
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Website URL
            </label>
            <input
              type="url"
              value={formData.website_url || ""}
              onChange={(e) => handleChange("website_url", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent"
              placeholder="https://www.yourdomain.com"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Redirect URL
            </label>
            <input
              type="url"
              value={formData.redirect_url || ""}
              onChange={(e) => handleChange("redirect_url", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent"
              placeholder="https://www.yourdomain.com"
            />
            <p className="mt-1 text-xs text-gray-400">
              Where unauthenticated users are redirected to (e.g., your
              marketing site).
            </p>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="cursor-pointer px-4 py-2 bg-[#374795] text-white text-sm font-medium rounded-lg hover:bg-[#2d3a7a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
};
